/**
 * Campaign-wizard draft persistence + the sessionStorage flags behind the
 * "leave the wizard to add leads, come back, nothing lost" flow.
 *
 * Why this exists (owner complaint, 2026-08-31): the campaign wizard and the
 * lead import were a chicken-and-egg dead end. Creating a campaign first hit
 * an empty lead picker with no way to add leads; importing leads first hit
 * "No campaigns to import to". Both directions now work by parking the
 * wizard's full form state in sessionStorage while the user detours to Find
 * Leads / Add with URL, then restoring it when the wizard reopens.
 *
 * Four one-shot, session-scoped keys (a closed tab cleans up after itself):
 *  - dca_wizard_draft     — the wizard's form state + current step, as a
 *                           versioned JSON payload (v: 2). Written on every
 *                           change while the wizard is open; cleared after a
 *                           successful create or an explicit "Discard draft".
 *  - dca_wizard_return    — "the user left mid-setup to add leads". The Leads
 *                           and Find Leads pages show a return banner while
 *                           this is set.
 *  - dca_wizard_preselect — lead ids to pre-select on the next wizard open.
 *                           Set by the Add-to-Campaign dialog when the user
 *                           has leads but zero campaigns.
 *  - dca_open_wizard      — pre-existing "open the wizard on arrival" flag
 *                           (onboarding hand-off, 2026-08-30) that the return
 *                           flow reuses; campaigns-list.tsx consumes it.
 *
 * Every storage touch goes through the safe* wrappers below: in private mode
 * or with site data blocked the sessionStorage accessor itself can throw, and
 * losing a convenience must never crash the app (same contract as the
 * existing dca_open_wizard handling in campaigns-list.tsx).
 *
 * The serialize/parse pair is deliberately pure (no storage access) so the
 * round-trip is unit-testable without a DOM.
 */

export const WIZARD_DRAFT_KEY = 'dca_wizard_draft'
export const WIZARD_RETURN_KEY = 'dca_wizard_return'
export const WIZARD_PRESELECT_KEY = 'dca_wizard_preselect'
export const OPEN_WIZARD_KEY = 'dca_open_wizard'

/**
 * Bump when the draft shape changes incompatibly. v2 (2026-10-28) reordered
 * the steps and dropped "One set for all steps"; v1 drafts are upgraded by
 * upgradeV1Draft below, anything else is ignored.
 */
export const DRAFT_VERSION = 2

/**
 * v1 step order: 1 Details, 2 Messaging (brief), 3 Leads, 4 Sequence
 * (writer choice + messages), 5 Preview, 6 Review.
 * v2 step order: 1 Details, 2 Leads, 3 Method, 4 Messages, 5 What the AI
 * knows (brief), 6 Preview and launch. v1's Sequence step held the writer
 * choice, so it lands on Method; Preview and Review merged into step 6.
 */
const V1_TO_V2_STEP: Readonly<Record<number, number>> = { 1: 1, 2: 5, 3: 2, 4: 3, 5: 6, 6: 6 }

const MAX_STEP_SAMPLES = 3

const nonEmptyStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : []

/**
 * Pure: carry a v1 draft into the v2 wizard. The step number is mapped onto
 * the new order, and samples typed under the retired "One set for all steps"
 * are copied onto every step that has none of its own (global mode gave them
 * to every step), so nothing the user wrote is lost. The v1-only keys are dropped.
 */
export function upgradeV1Draft(step: number, state: Record<string, unknown>): { step: number; state: Record<string, unknown> } {
  const { style_samples_mode: mode, style_samples_global: global, ...rest } = state
  const samples = nonEmptyStrings(global).slice(0, MAX_STEP_SAMPLES)
  const next: Record<string, unknown> = { ...rest }
  if (mode !== 'per_step' && samples.length > 0 && Array.isArray(rest.sequence_steps)) {
    next.sequence_steps = rest.sequence_steps.map((s: unknown) => {
      if (typeof s !== 'object' || s === null) return s
      const stepObj = s as { type?: unknown; style_samples?: unknown }
      if (nonEmptyStrings(stepObj.style_samples).length > 0) return s
      return { ...stepObj, style_samples: samples }
    })
  }
  return { step: V1_TO_V2_STEP[Math.trunc(step)] ?? 1, state: next }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    // sessionStorage unavailable (private mode) — behave as if empty
    return null
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // sessionStorage unavailable — the draft just isn't persisted
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // sessionStorage unavailable — nothing to remove
  }
}

export type WizardDraft<S> = {
  /** Wizard step (1-based) the user was on when the draft was saved. */
  readonly step: number
  /** The wizard's form state. Callers merge this over their defaults. */
  readonly state: S
}

/** Pure: turn the wizard's position + state into the versioned JSON payload. */
export function serializeWizardDraft<S>(step: number, state: S): string {
  return JSON.stringify({ v: DRAFT_VERSION, step, state })
}

/**
 * Pure: parse a stored draft payload. Returns null for anything that isn't a
 * well-formed v2 draft or an upgradable v1 draft (garbage JSON, unknown
 * versions, missing fields) so a stale or corrupted entry silently falls back
 * to a fresh wizard instead of crashing the open.
 */
export function parseWizardDraft<S>(raw: string | null): WizardDraft<S> | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const candidate = parsed as { v?: unknown; step?: unknown; state?: unknown }
    if (candidate.v !== DRAFT_VERSION && candidate.v !== 1) return null
    if (typeof candidate.step !== 'number') return null
    if (typeof candidate.state !== 'object' || candidate.state === null || Array.isArray(candidate.state)) return null
    if (candidate.v === 1) {
      const upgraded = upgradeV1Draft(candidate.step, candidate.state as Record<string, unknown>)
      return { step: upgraded.step, state: upgraded.state as S }
    }
    return { step: candidate.step, state: candidate.state as S }
  } catch {
    return null
  }
}

/** Persist the wizard's current position + state (no-op if storage throws). */
export function saveWizardDraft<S>(step: number, state: S): void {
  safeSessionSet(WIZARD_DRAFT_KEY, serializeWizardDraft(step, state))
}

/** Load the saved draft, or null if none / unreadable / wrong version. */
export function loadWizardDraft<S>(): WizardDraft<S> | null {
  return parseWizardDraft<S>(safeSessionGet(WIZARD_DRAFT_KEY))
}

/** Drop the draft — after a successful create or an explicit discard. */
export function clearWizardDraft(): void {
  safeSessionRemove(WIZARD_DRAFT_KEY)
}

/** Flag that the user left the wizard to go add leads (shows return banners). */
export function flagWizardReturn(): void {
  safeSessionSet(WIZARD_RETURN_KEY, '1')
}

/** Whether a wizard-return detour is in progress. */
export function hasWizardReturn(): boolean {
  return safeSessionGet(WIZARD_RETURN_KEY) === '1'
}

/** Clear the detour flag (banner dismissed, returned, or wizard finished). */
export function clearWizardReturn(): void {
  safeSessionRemove(WIZARD_RETURN_KEY)
}

/** Ask the campaigns page to auto-open the wizard on arrival (one-shot). */
export function requestWizardOpen(): void {
  safeSessionSet(OPEN_WIZARD_KEY, '1')
}

/** Stash lead ids for the wizard to pre-select on its next open. */
export function stashPreselectLeadIds(ids: readonly string[]): void {
  safeSessionSet(WIZARD_PRESELECT_KEY, JSON.stringify(ids))
}

/**
 * Read AND delete the pre-select stash (one-shot by design — a later,
 * unrelated wizard open must not resurrect an old selection). Returns [] for
 * anything that isn't a string array.
 */
export function takePreselectLeadIds(): readonly string[] {
  const raw = safeSessionGet(WIZARD_PRESELECT_KEY)
  safeSessionRemove(WIZARD_PRESELECT_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((id): id is string => typeof id === 'string')
  } catch {
    return []
  }
}
